/**
 * 版本下拉搜索。
 * 不含 * ：大小写不敏感子串。
 * 含 * ：整串通配。* 是任意字符序列（含空），可在任意位置，可多个。
 * 紧挨在 * 前面的点并进通配（.* 与 * 同义）。? 不是通配。
 * 不用正则。几十万条时：有字面前缀就二分，否则顺序扫；下拉只取前 MATCH_LIMIT 条。
 */

export const VERSION_SEARCH_LIMIT = 300;

function matchParts(value, parts, startsWithStar, endsWithStar) {
  let from = 0;
  if (!startsWithStar) {
    if (!value.startsWith(parts[0])) {
      return false;
    }
    from = parts[0].length;
  }
  const lastIndex = parts.length - 1;
  for (let i = startsWithStar ? 0 : 1; i < lastIndex; i += 1) {
    const part = parts[i];
    if (!part) {
      continue;
    }
    const idx = value.indexOf(part, from);
    if (idx < 0) {
      return false;
    }
    from = idx + part.length;
  }
  const last = parts[lastIndex];
  if (!last) {
    return true;
  }
  if (endsWithStar) {
    return value.indexOf(last, from) >= 0;
  }
  return value.endsWith(last) && value.length - last.length >= from;
}

function preparePattern(raw) {
  const lowered = raw == null ? '' : String(raw).toLowerCase();
  if (!lowered) {
    return null;
  }
  if (!lowered.includes('*')) {
    return {
      prefix: '',
      test: (value) => value.includes(lowered),
    };
  }
  const collapsed = lowered.replace(/\.\*/g, '*').replace(/\*+/g, '*');
  const parts = collapsed.split('*');
  const startsWithStar = collapsed.startsWith('*');
  const endsWithStar = collapsed.endsWith('*');
  return {
    prefix: startsWithStar ? '' : parts[0],
    test: (value) => matchParts(value, parts, startsWithStar, endsWithStar),
  };
}

export function matchesVersionSearch(text, pattern) {
  const prepared = preparePattern(pattern);
  if (!prepared) {
    return true;
  }
  const value = (text == null ? '' : String(text)).toLowerCase();
  return prepared.test(value);
}

function lowerBound(sorted, prefix) {
  let lo = 0;
  let hi = sorted.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid].text < prefix) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return lo;
}

/**
 * @param {{ text: string, nodeKey: string }[]} entries 版本号或别名，nodeKey 是树上的 key
 */
export function createVersionSearchIndex(entries) {
  const sorted = (entries || [])
    .map((entry) => ({
      text: entry.text == null ? '' : String(entry.text).toLowerCase(),
      nodeKey: entry.nodeKey,
    }))
    .filter((entry) => entry.text && entry.nodeKey != null)
    .sort((a, b) => (a.text < b.text ? -1 : a.text > b.text ? 1 : 0));

  return {
    size: sorted.length,
    /**
     * @returns {{ keys: string[], capped: boolean }}
     */
    search(pattern, limit) {
      const cap = limit == null ? VERSION_SEARCH_LIMIT : limit;
      const prepared = preparePattern(pattern);
      if (!prepared) {
        return { keys: [], capped: false };
      }
      const keys = [];
      const seen = new Set();
      const push = (nodeKey) => {
        if (seen.has(nodeKey)) {
          return false;
        }
        seen.add(nodeKey);
        keys.push(nodeKey);
        return keys.length >= cap;
      };
      const scan = (from, to) => {
        for (let i = from; i < to; i += 1) {
          if (prepared.test(sorted[i].text) && push(sorted[i].nodeKey)) {
            return true;
          }
        }
        return false;
      };
      if (prepared.prefix) {
        const lo = lowerBound(sorted, prepared.prefix);
        const hi = lowerBound(sorted, `${prepared.prefix}\uffff`);
        return { keys, capped: scan(lo, hi) };
      }
      return { keys, capped: scan(0, sorted.length) };
    },
  };
}
